import React, { useEffect, useMemo } from 'react'
import { Col, Row } from 'antd'
import { FieldArray } from 'formik'
import { useTranslation } from 'react-i18next'

import { Id, RequestCustomData } from 'src/types'

import { Button, ButtonTitled } from 'src/components/Ant'
import { SelectResource } from 'src/components/Formik'
import { useStepsContext } from 'src/views/Requests/views/Form'

import { WrapCard } from '../../../styles'

interface Props {
  enableContract: boolean
  enableTarget: boolean
  formikProps: any
  organization_id: Id
}

function AdditionalInformation({
  enableContract,
  enableTarget,
  formikProps,
  organization_id
}: Props): JSX.Element {
  const stepsContext = useStepsContext()
  const { t } = useTranslation()
  const requestCustomData = useMemo<RequestCustomData[]>(() => {
    return formikProps.values.request_custom_data.filter(
      (item: RequestCustomData) => item.active
    )
  }, [formikProps.values.request_custom_data])

  useEffect(() => {
    if (formikProps.submitCount) {
      if (typeof formikProps.errors.requested_on === 'string') {
        stepsContext.setAlert('setup', {
          message: t('web.setup.form.start-date.minimum-days-limit-top'),
          type: 'error'
        })
        return
      }
      if (typeof formikProps.errors.request_custom_data === 'string') {
        stepsContext.setAlert('setup', {
          message: t(formikProps.errors.request_custom_data),
          type: 'error'
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formikProps.errors.request_custom_data,
    formikProps.errors.requested_on,
    formikProps.submitCount
  ])

  return (
    <WrapCard title="web.setup.additional-information.title">
      <FieldArray
        name="request_custom_data"
        render={({ push, replace, remove }) => (
          <>
            {requestCustomData.map((item: RequestCustomData, index: number) => (
              <Row
                align="middle"
                gutter={24}
                key={item.request_custom_data_id || item.tmp_id}
                type="flex"
              >
                {enableTarget && (
                  <Col span={8}>
                    <SelectResource
                      formikProps={formikProps}
                      keyLabel="name"
                      keyValue="target_id"
                      label="web.assignment.billing.form.target.title"
                      name={`request_custom_data.${index}.target_id`}
                      organization_id={organization_id}
                      placeholder="web.assignment.billing.form.target.placeholder"
                      resource="targets"
                    />
                  </Col>
                )}
                {enableContract && (
                  <Col span={8}>
                    <SelectResource
                      defaultActiveFirstOption={false}
                      formikProps={formikProps}
                      keyLabel="name"
                      keyValue="contract_id"
                      label="web.assignment.sharing.form.contract.title"
                      name={`request_custom_data.${index}.contract_id`}
                      organization_id={organization_id}
                      placeholder="web.assignment.sharing.form.contract.placeholder"
                      resource="contracts"
                    />
                  </Col>
                )}
                <Col span={8}>
                  <ButtonTitled
                    icon="delete"
                    onClick={() => {
                      if (item.request_custom_data_id) {
                        replace(index, { ...item, active: false })
                      } else if (item.tmp_id) {
                        remove(index)
                      }
                    }}
                    shape="circle"
                    tooltip="common.actions.remove"
                    type="danger"
                  />
                </Col>
              </Row>
            ))}
            <Button
              block
              icon="plus"
              onClick={() =>
                push({
                  active: true,
                  contract: {
                    contract_id: '',
                    name: ''
                  },
                  contract_id: '',
                  target: {
                    target_id: '',
                    name: ''
                  },
                  target_id: '',
                  tmp_id: Math.random()
                })
              }
              title="common.actions.new"
              type="primary"
            />
          </>
        )}
      />
    </WrapCard>
  )
}

export default AdditionalInformation
